<template>
  <div class="widget w-simple-chart-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header printView :tradeButtonsSymbol="ric" :wid="wid">
      <c-incremental-search slot="left" :ric="ric" @error="onSymbolSearchError" @change-ric="onSetRIC" />
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar v-if="ric">
      <c-widget-control-bar-group>
        <v-form-group :label="$t('white-label.globalLabels.chart.type.label')" label-cols="auto">
          <v-form-select
            :value="chartType"
            :options="chartTypeOptions"
            @input="onChangeChartType"
            :aria-label="$t('white-label.globalLabels.chart.type.label')"
          />
        </v-form-group>
        <v-form-group :label="$t('white-label.globalLabels.chart.period.label')" label-cols="auto">
          <v-form-radio-group
            buttons
            button-variant="primary"
            :checked="timeRange"
            :options="timeRangeOptions"
            @input="onChangeTimeRange"
          />
        </v-form-group>
        <v-form-group label="" label-cols="auto">
          <v-form-radio-group
            buttons
            button-variant="primary"
            :checked="scaleType"
            :options="scaleTypeOptions"
            @input="onChangeScaleType"
          />
        </v-form-group>
      </c-widget-control-bar-group>
    </c-widget-control-bar>

    <div v-if="ric" class="widget-body">
      <c-loader v-if="isLoading" />
      <v-stock-chart
        v-if="isChartData"
        :class="['chart', { [chartType]: true, [scaleType]: true }]"
        :options="chartOptions"
        ref="chart"
      />
    </div>
    <v-alert v-else align="center" class="m-2" show variant="light">{{ i18n('nosymbol', 'globalMessages') }}</v-alert>
    <b-toaster :name="notifierId"></b-toaster>
  </div>
</template>

<script>
import baseMixin from './sharedFunctionality.mixin';
import widgetCommons from '@mixins/widgetCommons';
import CLoader from '@custom/CLoader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetControlBarGroup from '@custom/widgetContainer/CWidgetControlBarGroup';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CIncrementalSearch from '@custom/CIncrementalSearch';
import moment from 'moment';

export default {
  name: 'w-simple-chart-widget',
  mixins: [widgetCommons, baseMixin],
  components: {
    CLoader,
    CWidgetControlBar,
    CWidgetControlBarGroup,
    CWidgetHeader,
    CWidgetHeaderButton,
    CIncrementalSearch
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  computed: {
    isLoading() {
      return this.isChartLoading;
    },
    ric() {
      return this.$store.state.widget[this.wid].ric;
    },
    chartType() {
      // 'area', 'line'
      return this.$store.state.widget[this.wid].chartType;
    },
    scaleType() {
      return this.$store.state.widget[this.wid].scaleType;
    },
    timeRange() {
      return this.$store.state.widget[this.wid].timeRange;
    },
    chartTypeOptions() {
      const path = 'white-label.globalLabels.chart.type.options.';
      return [
        { value: 'line', text: this.$t(path + 'line') },
        { value: 'area', text: this.$t(path + 'area') },
        { value: 'ohlc', text: this.$t(path + 'bar') },
        { value: 'candlestick', text: this.$t(path + 'candlestick') }
      ];
    }
  },
  mounted() {
    this.chartOptions.lang.accessibility.chartContainerLabel = this.getChartTitle();
    this.chartOptions.lang.accessibility.svgContainerLabel = this.getChartTitle();
    this.chartOptions.lang.accessibility.defaultChartTitle = this.getChartTitle();
  },
  updated() {
    if (this.$refs.chart && this.$refs.chart.chart) {
      this.chartOptions.xAxis.accessibility.rangeDescription =
        'Range:' +
        moment(new Date(this.$refs.chart.chart.xAxis[0].min)).format('dddd MMMM Do YYYY, H:mm') +
        ' to ' +
        moment(new Date(this.$refs.chart.chart.xAxis[0].max)).format('dddd MMMM Do YYYY, H:mm');

      this.chartOptions.yAxis.accessibility.rangeDescription =
        'Range:' +
        this.$f(this.$refs.chart.chart.yAxis[0].min, 'float.price') +
        ' to ' +
        this.$f(this.$refs.chart.chart.yAxis[0].max, 'float.price');
    }
  },
  watch: {
    fontSize() {
      setTimeout(() => {
        // we need to reflow chart elements after font changed, but reflow doesn't happen, if the size of the chart container doesn't change. Let's kinda change it then
        this.$refs.chart.chart.container.parentNode.style.width =
          this.$refs.chart.chart.container.parentNode.offsetWidth - 2 + 'px';
        this.$refs.chart.reflow();
        this.$refs.chart.chart.container.parentNode.style.width = '';
      }, 1500);
    },
    ric: {
      handler() {
        if (this.ric) this.fetchChartData();
      },
      immediate: true
    },
    '$store.state.applicationSettings.general.settings.applicationLanguage'() {
      this.chartOptions.lang.accessibility.chartContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.svgContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.defaultChartTitle = this.getChartTitle();
    }
  },
  methods: {
    // ControlBar Events
    onSetRIC(val) {
      this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, val);
      this.sendLinkUpdate(val);
    },
    onChangeScaleType(value) {
      this.$store.commit(`widget/${this.wid}/SCALE_TYPE_CHANGED`, value);
    },
    onChangeTimeRange(timeRange) {
      this.$store.commit(`widget/${this.wid}/TIME_RANGE_CHANGED`, timeRange);
    },
    onChangeChartType(chartType) {
      this.$store.commit(`widget/${this.wid}/CHART_TYPE_CHANGED`, chartType);
      this.fetchChartData();
    },
    getChartTitle() {
      return this.wid
        ? `${this.$t(`white-label.widgets.simpleChart.widget-title`)} ${this.$t(
            'white-label.globalLabels.chartGraphImage'
          )}`
        : '';
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/simple-chart-widget';
</style>
